"use client"

import type React from "react"
import { useState, useRef, useEffect } from "react"
import { Sparkles, Loader2, RefreshCcw, Pencil } from "lucide-react"
import { Sheet, SheetClose, SheetContent, SheetHeader, SheetTitle } from "@workspace/web-ui/components/sheet"
import { Avatar, AvatarFallback, AvatarImage } from "@workspace/web-ui/components/avatar"
import { Button } from "@workspace/web-ui/components/button"
import { Checkbox } from "@workspace/web-ui/components/checkbox"
import { cn } from "@workspace/web-ui/lib/utils"
import AlfieImage from "~/assets/alfie-logo.svg"
import { useAuth } from "~/providers/auth.provider"
import { useChatMutation } from "../api/mutations/chat"
import { Label } from "@workspace/web-ui/components/label"
import { Input } from "@workspace/web-ui/components/input"
import { useOnboardingModal } from "~/features/onboarding/store/use-onboarding-modal"
import { useNavigate } from "@tanstack/react-router"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@workspace/web-ui/components/select"
import { conditions, pharmacies, redFlagQuestions } from "../data/booking-alfie-data"

interface Message {
  id: string
  type: "user" | "bot"
  content: string
  isLoading?: boolean
  suggestiveAnswers?: SuggestiveAnswer[]
  bookingStep?: number
  bookingData?: any
}

interface SuggestiveAnswer {
  id: string
  text: string
  type: "single" | "multiple"
}

interface DiagnosisData {
  condition: string
  description: string
  symptoms: string[]
  redFlags: string[]
  diagnosis: string[]
  causes: string[]
  prevention: string[]
  treatments: {
    category: string
    medication: string
    quantity: string
    instructions: string[]
    isPrimary: boolean
    isSecondary: boolean
    isExtra: boolean
  }[]
  imageUrls: string[]
}

interface ChatBotProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface UserDetails {
  name: string
  email: string
  state: string
  city: string
  dateOfBirth: string
  gender: string
}

interface ConditionAnswer {
  conditionId: string
  diagnosed: boolean
  diagnosedDate: string
  medications: string
}

interface ConditionQuestion {
  id: string
  text: string
  type: "boolean" | "date" | "text"
}

export interface Condition {
  id: string
  name: string
  questions: ConditionQuestion[]
}

interface Allergy {
  allergen: string
  severity: string
  medication: string
}

export function AlfieChatBot({ open, onOpenChange }: ChatBotProps) {
  const { mutate: chatMutation, isPending: isSendingMessage } = useChatMutation()
  const auth = useAuth()
  const { open: OpenOnboardingModal } = useOnboardingModal()
  const navigate = useNavigate()

  const [messages, setMessages] = useState<Message[]>([
    {
      id: "1",
      type: "bot",
      content: "Welcome to ProxyMedicine Urgent Care! I'm Alfie, your virtual health assistant.",
    },
    {
      id: "2",
      type: "bot",
      content: "Do you know your symptom, or would you like me to ask some questions?",
      suggestiveAnswers: [
        { id: "know_symptom", text: "I know my symptom", type: "single" },
        { id: "talk_to_alfie", text: "Talk to Alfie", type: "single" },
      ],
    },
  ])
  const [initialThreadId, setInitialThreadId] = useState<string | null>(null)
  const [currentAgent, setCurrentAgent] = useState<string>("alfie-chat")
  const [selectedAnswers, setSelectedAnswers] = useState<Record<string, string[]>>({})

  const [inputValue, setInputValue] = useState("")
  const [showDiagnosis, setShowDiagnosis] = useState(false)
  const [consentChecked, setConsentChecked] = useState(false)
  const [diagnosisData, setDiagnosisData] = useState<DiagnosisData | null>(null)

  // const [bookingStep, setBookingStep] = useState(0)
  const [userDetails, setUserDetails] = useState<UserDetails>({
    name: "",
    email: "",
    state: "",
    city: "",
    dateOfBirth: "",
    gender: "",
  })
  const [editMode, setEditMode] = useState(false)
  const [selectedPharmacy, setSelectedPharmacy] = useState<string | null>(null)
  const [selectedConditions, setSelectedConditions] = useState<string[]>([])
  const [conditionAnswers, setConditionAnswers] = useState<ConditionAnswer[]>([])
  const [currentConditionIndex, setCurrentConditionIndex] = useState(0)
  const [vitals, setVitals] = useState({
    bloodPressure: "",
    heartRate: "",
    dontKnowVitals: false,
  })
  const [allergies, setAllergies] = useState<Allergy[]>([])
  const [redFlagAnswers, setRedFlagAnswers] = useState<Record<string, boolean>>({})
  const [showUrgentCare, setShowUrgentCare] = useState(false)
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [showBookingFlow, setShowBookingFlow] = useState(false)
  const [activeBookingMessageId, setActiveBookingMessageId] = useState<string | null>(null)
  const [checkInAnswers, setCheckInAnswers] = useState<Record<string, boolean>>({})
  const [activeTab, setActiveTab] = useState<'causes' | 'diagnosis' | 'treatment'>('causes')
  const [showAllergyInputs, setShowAllergyInputs] = useState(false)

  const messagesEndRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages])

  const handleSuggestiveAnswerSelection = (messageId: string, answerId: string, answerType: "single" | "multiple") => {
    setSelectedAnswers((prev) => {
      const currentSelections = prev[messageId] || []

      if (answerType === "single") {
        return { ...prev, [messageId]: [answerId] }
      } else {
        const newSelections = currentSelections.includes(answerId)
          ? currentSelections.filter((id) => id !== answerId)
          : [...currentSelections, answerId]
        return { ...prev, [messageId]: newSelections }
      }
    })
  }

  const handleSuggestiveAnswerSubmit = (messageId: string) => {
    const selections = selectedAnswers[messageId] || []
    if (selections.length === 0) return

    const message = messages.find((m) => m.id === messageId)
    if (!message?.suggestiveAnswers) return

    const selectedTexts = selections
      .map((id) => message.suggestiveAnswers?.find((answer) => answer.id === id)?.text)
      .filter(Boolean)

    let agentToUse = currentAgent
    if (selections.includes("talk_to_alfie")) {
      agentToUse = "alfie"
      setCurrentAgent("alfie")
    } else if (selections.includes("know_symptom")) {
      agentToUse = "vector"
      setCurrentAgent("vector")
    }

    const userMessageId = Date.now().toString()
    setMessages((prev) => [
      ...prev,
      {
        id: userMessageId,
        type: "user",
        content: selectedTexts.join(", "),
      },
    ])

    const tempLoadingId = (Date.now() + 1).toString()
    setMessages((prev) => [
      ...prev,
      {
        id: tempLoadingId,
        type: "bot",
        content: "",
        isLoading: true,
      },
    ])

    chatMutation(
      {
        message: selectedTexts.join(", "),
        thread_id: initialThreadId || "",
        agent: agentToUse,
      },
      {
        onSuccess: (response) => {
          handleChatResponse(response, tempLoadingId, agentToUse)
        },
        onError: (error) => {
          handleChatError(error, tempLoadingId)
        },
      },
    )

    setSelectedAnswers((prev) => ({ ...prev, [messageId]: [] }))
  }

  const handleSendMessage = () => {
    if (!inputValue.trim() || isSendingMessage) return

    const userMessageId = Date.now().toString()
    setMessages((prev) => [
      ...prev,
      {
        id: userMessageId,
        type: "user",
        content: inputValue,
      },
    ])

    const tempLoadingId = (Date.now() + 1).toString()
    setMessages((prev) => [
      ...prev,
      {
        id: tempLoadingId,
        type: "bot",
        content: "",
        isLoading: true,
      },
    ])

    const currentInput = inputValue
    setInputValue("")

    chatMutation(
      {
        message: currentInput || "",
        thread_id: initialThreadId || "",
        agent: currentAgent,
      },
      {
        onSuccess: (response) => {
          handleChatResponse(response, tempLoadingId, currentAgent)
        },
        onError: (error) => {
          handleChatError(error, tempLoadingId)
        },
      },
    )
  }

  const handleChatResponse = (response: any, tempLoadingId: string, agentUsed: string) => {
    console.log("Agent Used", agentUsed)
    if (!response.success || !response.data) {
      setMessages((prev) =>
        prev
          .filter((msg) => msg.id !== tempLoadingId)
          .concat({
            id: Date.now().toString(),
            type: "bot",
            content: response.error || "Sorry, I'm having trouble connecting right now. Please try again later.",
          }),
      )
      return
    }

    const responseData = response.data

    const messageContent =
      responseData.follow_up_question && responseData.follow_up_question.question
        ? typeof responseData.follow_up_question.question === "string"
          ? responseData.follow_up_question.question
          : JSON.stringify(responseData.follow_up_question.question)
        : "No response content"

    let suggestiveAnswers: SuggestiveAnswer[] | undefined = undefined
    if (responseData.follow_up_question.options && Array.isArray(responseData.follow_up_question.options)) {
      suggestiveAnswers = responseData.follow_up_question.options.map((answer: any, index: number) => ({
        id: answer || `answer_${index}`,
        text: answer || answer,
        type: answer.type || "single",
      }))
    }

    setMessages((prev) =>
      prev
        .filter((msg) => msg.id !== tempLoadingId)
        .concat({
          id: responseData.run_id || Date.now().toString(),
          type: "bot",
          content: messageContent,
          suggestiveAnswers,
        } as Message),
    )

    setInitialThreadId(response.data?.thread_id || null)

    if (responseData.end || responseData.disease) {
      console.log(responseData.disease)
      const hasValidDiseaseData = (disease: any): boolean => {
        return !!(
          disease?.disease_name ||
          disease?.overview ||
          disease?.symptoms?.length > 0 ||
          disease?.red_flags?.length > 0 ||
          disease?.diagnosis?.length > 0 ||
          disease?.causes?.length > 0 ||
          disease?.prevention?.length > 0 ||
          disease?.treatment_page?.length > 0 ||
          disease?.image_urls?.length > 0
        )
      }

      const extractedDiagnosis: DiagnosisData | null = hasValidDiseaseData(responseData.disease)
        ? {
            condition: responseData.disease.disease_name || "Unknown Condition",
            description: responseData.disease.overview || "No description available",
            symptoms: responseData.disease.symptoms || [],
            redFlags: responseData.disease.red_flags || [],
            diagnosis: responseData.disease.diagnosis || [],
            causes: responseData.disease.causes || [],
            prevention: responseData.disease.prevention || [],
            treatments: responseData.disease.treatment_page || [],
            imageUrls: responseData.disease.image_urls || [],
          }
        : null

      setDiagnosisData(extractedDiagnosis)
      setShowDiagnosis(true)
    }
  }

  const handleChatError = (error: any, tempLoadingId: string) => {
    console.error("Error sending message:", error)

    setMessages((prev) =>
      prev
        .filter((msg) => msg.id !== tempLoadingId)
        .concat({
          id: Date.now().toString(),
          type: "bot",
          content: "Sorry, I'm having trouble connecting right now. Please try again later.",
        }),
    )
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const startBookingFlow = () => {
    setShowBookingFlow(true)
    // setBookingStep(0)

    if (auth?.user) {
      setUserDetails({
        name: auth.user.first_name || "Maurice Umoh",
        email: auth.user.email || "Mauriceumoh47@gmail.com",
        state: auth.user.state || "Lagos",
        city: auth.user.city || "Lekki",
        dateOfBirth: auth.user.date_of_birth || "06/15/1965",
        gender: auth.user.gender || "Male",
      })
    }

    const userMessageId = Date.now().toString()
    const botMessageId = (Date.now() + 1).toString()

    setMessages((prev) => [
      ...prev,
      {
        id: userMessageId,
        type: "user",
        content: "I'd like to proceed with the consultation booking",
      },
      {
        id: botMessageId,
        type: "bot",
        content: "Great! Let's get you set up for your consultation. I'll need to confirm some details with you.",
      },
    ])

    setTimeout(() => {
      const bookingMessageId = Date.now().toString()
      setMessages((prev) => [
        ...prev,
        {
          id: bookingMessageId,
          type: "bot",
          content: "Let's confirm your details before we proceed.",
          bookingStep: 0,
          bookingData: { userDetails },
        },
      ])
      setActiveBookingMessageId(bookingMessageId)
    }, 500)
  }

  const handleDetailsConfirm = () => {
    const userMessageId = Date.now().toString()
    setMessages((prev) => [
      ...prev,
      {
        id: userMessageId,
        type: "user",
        content: "Yes, my details are correct",
      },
    ])

    // setBookingStep(1)
    setTimeout(() => {
      const bookingMessageId = Date.now().toString()
      setMessages((prev) => [
        ...prev,
        {
          id: bookingMessageId,
          type: "bot",
          content: "Select a preferred pharmacy close to your current location for pick up or delivery.",
          bookingStep: 1,
        },
      ])
      setActiveBookingMessageId(bookingMessageId)
    }, 500)
  }

  const handlePharmacySelect = (pharmacyId: string) => {
    setSelectedPharmacy(pharmacyId)
  }

  const handlePharmacyConfirm = () => {
    const selectedPharmacyObj = pharmacies.find((p) => p.id === selectedPharmacy)
    const selectedPharmacyName = selectedPharmacyObj?.name || "Selected pharmacy"
    const selectedPharmacyAddress = selectedPharmacyObj?.address || ""

    const userMessageId = Date.now().toString()
    setMessages((prev) => [
      ...prev,
      {
        id: userMessageId,
        type: "user",
        content: `${selectedPharmacyName} - ${selectedPharmacyAddress}`,
      },
    ])

    // setBookingStep(2)
    setTimeout(() => {
      const bookingMessageId = Date.now().toString()
      setMessages((prev) => [
        ...prev,
        {
          id: bookingMessageId,
          type: "bot",
          content: "Which of these conditions apply to you? Please select all that apply.",
          bookingStep: 2,
        },
      ])
      setActiveBookingMessageId(bookingMessageId)
    }, 500)
  }

  const handleConditionToggle = (conditionId: string) => {
    setSelectedConditions((prev) =>
      prev.includes(conditionId) ? prev.filter((id) => id !== conditionId) : [...prev, conditionId],
    )
  }

  const handleConditionsConfirm = () => {
    if (selectedConditions.length > 0) {
      const conditionNames = selectedConditions
        .map((id) => conditions.find((c) => c.id === id)?.name)
        .filter(Boolean)
        .join(", ")

      const userMessageId = Date.now().toString()
      setMessages((prev) => [
        ...prev,
        {
          id: userMessageId,
          type: "user",
          content: `${conditionNames}`,
        },
      ])

      const initialAnswers = selectedConditions.map((conditionId) => ({
        conditionId,
        diagnosed: false,
        diagnosedDate: "",
        medications: "",
      }))
      setConditionAnswers(initialAnswers)

      // setBookingStep(3)
      setCurrentConditionIndex(0)

      setTimeout(() => {
        const currentConditionId = selectedConditions[0]
        const currentCondition = conditions.find((c) => c.id === currentConditionId)

        if (currentCondition) {
          const bookingMessageId = Date.now().toString()
          setMessages((prev) => [
            ...prev,
            {
              id: bookingMessageId,
              type: "bot",
              content: `You selected ${selectedConditions.length} conditions, please provide details for them.`,
              bookingStep: 3,
              bookingData: {
                conditionIndex: 0,
                condition: currentCondition,
              },
            },
          ])
          setActiveBookingMessageId(bookingMessageId)
        }
      }, 500)
    }
  }

  const handleConditionQuestionChange = (field: keyof ConditionAnswer, value: string | boolean) => {
    setConditionAnswers((prev) => {
      const newAnswers = [...prev]
      const currentConditionId = selectedConditions[currentConditionIndex]
      const answerIndex = newAnswers.findIndex((a) => a.conditionId === currentConditionId)

      if (answerIndex !== -1 && currentConditionId) {
        newAnswers[answerIndex] = {
          conditionId: currentConditionId,
          diagnosed: false,
          diagnosedDate: "",
          medications: "",
          ...newAnswers[answerIndex],
          [field]: value,
        }
      }

      return newAnswers
    })
  }

  const handleNextCondition = () => {
    const currentConditionId = selectedConditions[currentConditionIndex]
    const currentCondition = conditions.find((c) => c.id === currentConditionId)
    const currentAnswer = conditionAnswers.find((a) => a.conditionId === currentConditionId)

    const userMessageId = Date.now().toString()
    let responseContent = `Have you been formally diagnosed with ${currentCondition?.name}? ${currentAnswer?.diagnosed ? "Yes" : "No"}`

    if (currentAnswer?.diagnosed) {
      responseContent += `\nWhen diagnosed: ${currentAnswer.diagnosedDate || "Not specified"}`
      responseContent += `\nMedications: ${currentAnswer.medications || "None"}`
    }

    setMessages((prev) => [
      ...prev,
      {
        id: userMessageId,
        type: "user",
        content: responseContent,
      },
    ])

    if (currentConditionIndex < selectedConditions.length - 1) {
      const nextIndex = currentConditionIndex + 1
      setCurrentConditionIndex(nextIndex)

      setTimeout(() => {
        const nextConditionId = selectedConditions[nextIndex]
        const nextCondition = conditions.find((c) => c.id === nextConditionId)

        if (nextCondition) {
          const bookingMessageId = Date.now().toString()
          setMessages((prev) => [
            ...prev,
            {
              id: bookingMessageId,
              type: "bot",
              content: `Now let's talk about ${nextCondition.name}.`,
              bookingStep: 3,
              bookingData: {
                conditionIndex: nextIndex,
                condition: nextCondition,
              },
            },
          ])
          setActiveBookingMessageId(bookingMessageId)
        }
      }, 500)
    } else {
      // setBookingStep(4)
      setTimeout(() => {
        const bookingMessageId = Date.now().toString()
        setMessages((prev) => [
          ...prev,
          {
            id: bookingMessageId,
            type: "bot",
            content: "Now let's talk vitals. Please enter your current blood pressure and heart rate.",
            bookingStep: 4,
          },
        ])
        setActiveBookingMessageId(bookingMessageId)
      }, 500)
    }
  }

  const handleVitalsChange = (field: keyof typeof vitals, value: string | boolean) => {
    setVitals((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleVitalsConfirm = () => {
    const vitalsResponse = vitals.dontKnowVitals
      ? "I don't know my vitals"
      : `Blood pressure: ${vitals.bloodPressure}, Heart rate: ${vitals.heartRate}`

    const userMessageId = Date.now().toString()
    setMessages((prev) => [
      ...prev,
      {
        id: userMessageId,
        type: "user",
        content: vitalsResponse,
      },
    ])

    // setBookingStep(5)
    setTimeout(() => {
      const bookingMessageId = Date.now().toString()
      setMessages((prev) => [
        ...prev,
        {
          id: bookingMessageId,
          type: "bot",
          content: "Do you have any allergies? Please list them below.",
          bookingStep: 5,
        },
      ])
      setActiveBookingMessageId(bookingMessageId)
    }, 500)
  }

  const handleAllergiesConfirm = () => {
    // Add user message for allergies
    const userMessageId = Date.now().toString()
    let allergyMessage = "No allergies"
    if (allergies.length > 0) {
      allergyMessage = allergies.map(allergy => 
        `${allergy.allergen} (${allergy.severity})${allergy.medication ? ` - Medication: ${allergy.medication}` : ''}`
      ).join(', ')
    }
    setMessages(prev => [
      ...prev,
      {
        id: userMessageId,
        type: "user",
        content: allergyMessage
      }
    ])

    // Move to quick optional questions
    // setBookingStep(5.8)
    setTimeout(() => {
      const bookingMessageId = Date.now().toString()
      setMessages((prev) => [
        ...prev,
        {
          id: bookingMessageId,
          type: "bot",
          content: "Before we move on, I've got a few quick (optional) check-in questions, just to make sure we've got the full picture.",
          bookingStep: 5.8,
        },
      ])
      setActiveBookingMessageId(bookingMessageId)
    }, 500)
  }

  const handleRedFlagChange = (questionId: string, value: boolean) => {
    setRedFlagAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }))

    if (value === true) {
      setShowUrgentCare(true)
    } else {
      const updatedAnswers = { ...redFlagAnswers, [questionId]: value }
      const allNo = Object.values(updatedAnswers).every((answer) => answer === false)

      if (allNo && Object.keys(updatedAnswers).length === redFlagQuestions.length) {
        setShowUrgentCare(false)
      }
    }
  }

  const handleRedFlagConfirm = () => {
    if (showUrgentCare) {
      return
    }

    const redFlagResponse = Object.entries(redFlagAnswers)
      .map(([key, value]) => {
        const question = redFlagQuestions.find((q) => q.id === key)
        return `${question?.text} ${value ? "Yes" : "No"}`
      })
      .join("\n")

    const userMessageId = Date.now().toString()
    setMessages((prev) => [
      ...prev,
      {
        id: userMessageId,
        type: "user",
        content: redFlagResponse,
      },
    ])

    // setBookingStep(7)
    setTimeout(() => {
      const bookingMessageId = Date.now().toString()
      setMessages((prev) => [
        ...prev,
        {
          id: bookingMessageId,
          type: "bot",
          content: "Please review your diagnosis and provide consent to proceed with the consultation.",
          bookingStep: 7,
          bookingData: { diagnosisData },
        },
      ])
      setActiveBookingMessageId(bookingMessageId)
    }, 500)
  }

  const handleBookingComplete = () => {
    const bookingData = {
      userDetails,
      pharmacy: pharmacies.find((p) => p.id === selectedPharmacy),
      conditions: selectedConditions.map((conditionId) => {
        const condition = conditions.find((c) => c.id === conditionId)
        const answers = conditionAnswers.find((a) => a.conditionId === conditionId)
        return {
          ...condition,
          answers,
        }
      }),
      vitals,
      allergies,
      redFlagAnswers,
    }

    console.log("Booking completed with data:", bookingData)

    const userMessageId = Date.now().toString()
    setMessages((prev) => [
      ...prev,
      {
        id: userMessageId,
        type: "user",
        content: "I'd like to proceed with the consultation",
      },
    ])

    const confirmationId = Date.now() + 1
    setMessages((prev) => [
      ...prev,
      {
        id: confirmationId.toString(),
        type: "bot",
        content:
          "Great! Your consultation has been booked. Our healthcare provider will review your information and provide appropriate care. You'll be notified when your consultation is ready.",
      },
    ])

    setShowBookingFlow(false)
    setActiveBookingMessageId(null)

    setTimeout(() => {
      navigate({ to: "/dashboard/urgent-care" })
      setTimeout(() => onOpenChange(false), 1000)
    }, 2000)
  }

  const proceedToBooking = () => {
    if (auth?.user?.user_id) {
      startBookingFlow()
    } else {
      OpenOnboardingModal()
    }
  }

  const handleRetryQuestion = (step: number) => {
    console.log("Retrying question for step", step)
    const bookingMessageId = Date.now().toString()

    let content = ""
    let bookingData = null

    switch (step) {
      case 1:
        content = "Select a preferred pharmacy close to your current location for pick up or delivery."
        break
      case 2:
        content = "Which of these conditions apply to you? Please select all that apply."
        break
      case 3:
        const currentConditionId = selectedConditions[currentConditionIndex]
        const currentCondition = conditions.find((c) => c.id === currentConditionId)
        content = `Let's talk about ${currentCondition?.name}.`
        bookingData = {
          conditionIndex: currentConditionIndex,
          condition: currentCondition,
        }
        break
      case 4:
        content = "Now let's talk vitals. Please enter your current blood pressure and heart rate."
        break
      case 5:
        content = "Do you have any allergies? Please list them below."
        break
      case 6:
        content = "Please answer these red flag questions:"
        break
      default:
        content = "Let's continue with your booking."
    }

    setMessages((prev) => [
      ...prev,
      {
        id: bookingMessageId,
        type: "bot",
        content: content,
        bookingStep: step,
        bookingData: bookingData,
      },
    ])
    setActiveBookingMessageId(bookingMessageId)
  }

  const renderBookingStepUI = (message: Message) => {
    if (!message.bookingStep && message.bookingStep !== 0) return null

    switch (message.bookingStep) {
      case 0:
        return (
          <div className="mt-2 bg-gray-50 p-4 rounded-lg">
            {!editMode ? (
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Name</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm">{userDetails.name}</span>
                    <button onClick={() => setEditMode(true)} className="text-gray-500">
                      <Pencil className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Email:</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm">{userDetails.email}</span>
                    <button onClick={() => setEditMode(true)} className="text-gray-500">
                      <Pencil className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">State</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm">{userDetails.state}</span>
                    <button onClick={() => setEditMode(true)} className="text-gray-500">
                      <Pencil className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">City</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm">{userDetails.city}</span>
                    <button onClick={() => setEditMode(true)} className="text-gray-500">
                      <Pencil className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Date of Birth:</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm">{userDetails.dateOfBirth}</span>
                    <button onClick={() => setEditMode(true)} className="text-gray-500">
                      <Pencil className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Gender</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm">{userDetails.gender}</span>
                    <button onClick={() => setEditMode(true)} className="text-gray-500">
                      <Pencil className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <Button className="w-full bg-blue-500 hover:bg-blue-600 mt-4" onClick={handleDetailsConfirm}>
                  Yes, my details are correct
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First name</Label>
                    <Input id="firstName" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last name</Label>
                    <Input id="lastName" value={lastName} onChange={(e) => setLastName(e.target.value)} />
                  </div>
                </div>
                <div className="flex items-center justify-center w-full bg-white">
                <div className="fixed flex items-center justify-center p-4 bottom-2 w-[300px] md:w-[600px] bg-white ">
                <Button
                  className="w-full bg-blue-500 hover:bg-blue-600"
                  onClick={() => {
                    if (firstName && lastName) {
                      setUserDetails((prev) => ({
                        ...prev,
                        name: `${firstName} ${lastName}`,
                      }))
                      setEditMode(false)
                    }
                  }}
                >
                  Save
                </Button>
                </div>
                </div>
              </div>
            )}
          </div>
        )

      case 1:
        return (
          <div className="mt-2 bg-gray-50 p-4 rounded-lg">

            <div className="space-y-2">
              {pharmacies.map((pharmacy) => (
                <div
                  key={pharmacy.id}
                  className="flex items-center space-x-2 py-2"
                  onClick={() => handlePharmacySelect(pharmacy.id)}
                >
                  <div
                    className={cn(
                      "w-5 h-5 rounded-full border-2 flex items-center justify-center",
                      selectedPharmacy === pharmacy.id ? "border-blue-500" : "border-gray-400",
                    )}
                  >
                    {selectedPharmacy === pharmacy.id && <div className="w-2.5 h-2.5 bg-blue-500 rounded-full" />}
                  </div>
                  <span className="text-sm">
                    {pharmacy.name} - {pharmacy.address}
                  </span>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-center w-full bg-white">
            <div className="fixed flex items-center justify-center p-4 bottom-2 w-[350px] md:w-[600px] bg-white ">
            <Button
              className="w-full bg-blue-500 hover:bg-blue-600 mt-4"
              onClick={handlePharmacyConfirm}
              disabled={!selectedPharmacy}
            >
              Save
            </Button>
            </div>
            </div>
          </div>
        )

      case 2:
        return (
          <div className="mt-2 bg-gray-50 px-4 pt-4 pb-20 rounded-lg relative min-h-[200px]">
            <div className="grid grid-cols-2 gap-2 pb-16">
              {conditions.map((condition) => (
                <div key={condition.id} className="flex items-center space-x-2 py-1">
                  <Checkbox
                    id={`condition-${condition.id}`}
                    checked={selectedConditions.includes(condition.id)}
                    onCheckedChange={() => handleConditionToggle(condition.id)}
                  />
                  <Label htmlFor={`condition-${condition.id}`} className="text-sm">
                    {condition.name}
                  </Label>
                </div>
              ))}
            </div>

           <div className="flex items-center justify-center w-full bg-white">
            <div className="fixed flex items-center justify-center p-4 bottom-2 w-[350px] md:w-[600px] bg-white ">
              <Button
                className="w-[90%] bg-blue-500 hover:bg-blue-600"
                onClick={handleConditionsConfirm}
                disabled={selectedConditions.length === 0}
              >
                Continue
              </Button>
            </div>
           </div>
          </div>
        )

      case 3:
        if (message.bookingData?.condition) {
          const currentCondition = message.bookingData.condition
          const currentConditionId = currentCondition.id
          const currentAnswers = conditionAnswers.find((a) => a.conditionId === currentConditionId)

          if (!currentAnswers) return null

          return (
            <div className="mt-2 bg-gray-50 p-4 rounded-lg">

              <div className="space-y-4">
                {currentCondition.questions.map((question: ConditionQuestion) => {
                  if (question.type === "boolean") {
                    return (
                      <div key={question.id} className="space-y-2">
                        <p className="text-sm">{question.text}</p>
                        <div className="flex space-x-4">
                          <div
                            className="flex items-center space-x-2"
                            onClick={() => handleConditionQuestionChange("diagnosed", true)}
                          >
                            <div
                              className={cn(
                                "w-5 h-5 rounded-full border-2 flex items-center justify-center",
                                currentAnswers.diagnosed ? "border-blue-500" : "border-gray-400",
                              )}
                            >
                              {currentAnswers.diagnosed && <div className="w-2.5 h-2.5 bg-blue-500 rounded-full" />}
                            </div>
                            <span>Yes</span>
                          </div>
                          <div
                            className="flex items-center space-x-2"
                            onClick={() => handleConditionQuestionChange("diagnosed", false)}
                          >
                            <div
                              className={cn(
                                "w-5 h-5 rounded-full border-2 flex items-center justify-center",
                                !currentAnswers.diagnosed ? "border-blue-500" : "border-gray-400",
                              )}
                            >
                              {!currentAnswers.diagnosed && <div className="w-2.5 h-2.5 bg-blue-500 rounded-full" />}
                            </div>
                            <span>No</span>
                          </div>
                        </div>
                      </div>
                    )
                  } else if (question.type === "date") {
                    return (
                      <div key={question.id} className="space-y-2">
                        <Label htmlFor={question.id} className="text-sm">
                          {question.text}
                        </Label>
                        <Input
                          id={question.id}
                          placeholder="E.g 2023, 5 years ago"
                          value={currentAnswers.diagnosedDate}
                          onChange={(e) => handleConditionQuestionChange("diagnosedDate", e.target.value)}
                          disabled={!currentAnswers.diagnosed}
                        />
                      </div>
                    )
                  } else if (question.type === "text") {
                    return (
                      <div key={question.id} className="space-y-2">
                        <Label htmlFor={question.id} className="text-sm">
                          {question.text}
                        </Label>
                        <Input
                          id={question.id}
                          placeholder="Enter medication name"
                          value={currentAnswers.medications}
                          onChange={(e) => handleConditionQuestionChange("medications", e.target.value)}
                          disabled={!currentAnswers.diagnosed}
                        />
                      </div>
                    )
                  }
                  return null
                })}
              </div>

              <div className="flex items-center justify-center w-full bg-white">
              <div className="fixed flex items-center justify-center p-4 bottom-2 w-[350px] md:w-[600px] bg-white ">
              <Button className="w-full bg-blue-500 hover:bg-blue-600 mt-4" onClick={handleNextCondition}>
                Next condition
              </Button>
              </div>
              </div>

              <div className="text-center text-sm text-gray-500 mt-2">
                Condition {currentConditionIndex + 1} of {selectedConditions.length}
              </div>
            </div>
          )
        }
        return null

      case 4:
        return (
          <div className="mt-2 bg-gray-50 p-4 rounded-lg">

            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="bloodPressure" className="text-sm">
                  Blood pressure
                </Label>
                <Input
                  id="bloodPressure"
                  placeholder="e.g., 120/80"
                  value={vitals.bloodPressure}
                  onChange={(e) => handleVitalsChange("bloodPressure", e.target.value)}
                  disabled={vitals.dontKnowVitals}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="heartRate" className="text-sm">
                  Heart rate
                </Label>
                <Input
                  id="heartRate"
                  placeholder="e.g., 70 bpm"
                  value={vitals.heartRate}
                  onChange={(e) => handleVitalsChange("heartRate", e.target.value)}
                  disabled={vitals.dontKnowVitals}
                />
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="dontKnowVitals"
                  checked={vitals.dontKnowVitals}
                  onCheckedChange={(checked) => handleVitalsChange("dontKnowVitals", !!checked)}
                />
                <Label htmlFor="dontKnowVitals" className="text-sm">
                  I don't know my vitals
                </Label>
              </div>
            </div>

            <div className="flex items-center justify-center w-full bg-white">
            <div className="fixed flex items-center justify-center p-4 bottom-2 w-[350px] md:w-[600px] bg-white ">
            <Button className="w-full bg-blue-500 hover:bg-blue-600 mt-4" onClick={handleVitalsConfirm}>
              Continue
            </Button>
            </div>
            </div>
          </div>
        )

      case 5:
        return (
          <div className="mt-2 bg-gray-50 px-4 pt-4 pb-20 rounded-lg">
            <div className="space-y-4">
              <div className="flex space-x-4 mb-2">
                <div
                  className="flex items-center space-x-2 cursor-pointer"
                  onClick={() => setShowAllergyInputs(true)}
                >
                  <div
                    className={cn(
                      "w-5 h-5 rounded-full border-2 flex items-center justify-center",
                      showAllergyInputs ? "border-blue-500 bg-blue-500" : "border-gray-400",
                    )}
                  >
                    {showAllergyInputs && <div className="w-2.5 h-2.5 bg-white rounded-full" />}
                  </div>
                  <span>Yes</span>
                </div>
                <div
                  className="flex items-center space-x-2 cursor-pointer"
                  onClick={() => { setShowAllergyInputs(false); setAllergies([]); }}
                >
                  <div
                    className={cn(
                      "w-5 h-5 rounded-full border-2 flex items-center justify-center",
                      !showAllergyInputs ? "border-blue-500 bg-blue-500" : "border-gray-400",
                    )}
                  >
                    {!showAllergyInputs && <div className="w-2.5 h-2.5 bg-white rounded-full" />}
                  </div>
                  <span>No</span>
                </div>
              </div>

              {allergies.map((allergy, index) => (
                <div key={index} className="border rounded-lg p-3">
                  <p className="text-sm font-medium">Allergy {index + 1}:</p>
                  <p className="text-sm">Allergen: {allergy.allergen}</p>
                  <p className="text-sm">Severity: {allergy.severity}</p>
                  <p className="text-sm">Medication: {allergy.medication || 'None'}</p>
                </div>
              ))}

              <div className="space-y-2">
                <Label htmlFor="allergen" className="text-sm">
                  Allergen
                </Label>
                <Input
                  id="allergen"
                  placeholder="e.g., Pollen, Insulin"
                  value={newAllergy.allergen}
                  onChange={(e) => setNewAllergy({ ...newAllergy, allergen: e.target.value })}
                  disabled={!showAllergyInputs}
                  className={cn(!showAllergyInputs && "bg-gray-100")}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="severity" className="text-sm">
                  Severity
                </Label>
                <Select
                  value={newAllergy.severity}
                  onValueChange={(value) => setNewAllergy({ ...newAllergy, severity: value })}
                  disabled={!showAllergyInputs}
                >
                  <SelectTrigger className={cn(!showAllergyInputs && "bg-gray-100")}>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mild">Mild</SelectItem>
                    <SelectItem value="moderate">Moderate</SelectItem>
                    <SelectItem value="severe">Severe</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="allergyMedication" className="text-sm">
                  Medication for allergy (if any)
                </Label>
                <Input
                  id="allergyMedication"
                  placeholder="Enter medication"
                  value={newAllergy.medication}
                  onChange={(e) => setNewAllergy({ ...newAllergy, medication: e.target.value })}
                  disabled={!showAllergyInputs}
                  className={cn(!showAllergyInputs && "bg-gray-100")}
                />
              </div>
            </div>
            <div className="flex items-center justify-center w-full bg-white">
            <div className="fixed flex items-center justify-center p-4 bottom-2 w-[350px] md:w-[600px] bg-white ">
              <Button
                className="w-[90%] bg-blue-500 hover:bg-blue-600"
                onClick={handleAddAllergy}
                disabled={showAllergyInputs && (!newAllergy.allergen || !newAllergy.severity)}
              >
              {showAllergyInputs ? 'Add Allergy' : 'Continue'}
              </Button>
            </div>
           </div>
          </div>
        )

      case 5.5:
        return (
          <div className="mt-2 bg-gray-50 px-4 pt-4 pb-20 rounded-lg">
            <div className="flex space-x-4 mb-4">
              <div
                className="flex items-center space-x-2 cursor-pointer"
                onClick={() => {
                  setAddAnotherAllergySelected(true)
                  setNewAllergy({ allergen: '', severity: '', medication: '' })
                }}
              >
                <div
                  className={cn(
                    "w-5 h-5 rounded-full border-2 flex items-center justify-center",
                    addAnotherAllergySelected ? "border-blue-500 bg-blue-500" : "border-gray-400"
                  )}
                >
                  {addAnotherAllergySelected && <div className="w-2.5 h-2.5 bg-white rounded-full" />}
                </div>
                <span>Yes</span>
              </div>
              <div
                className="flex items-center space-x-2 cursor-pointer"
                onClick={() => {
                  setAddAnotherAllergySelected(false)
                }}
              >
                <div
                  className={cn(
                    "w-5 h-5 rounded-full border-2 flex items-center justify-center",
                    !addAnotherAllergySelected ? "border-blue-500 bg-blue-500" : "border-gray-400"
                  )}
                >
                  {!addAnotherAllergySelected && <div className="w-2.5 h-2.5 bg-white rounded-full" />}
                </div>
                <span>No</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="allergen" className="text-sm">
                Allergen
              </Label>
              <Input
                id="allergen"
                placeholder="e.g., Pollen, Insulin"
                value={newAllergy.allergen}
                onChange={(e) => setNewAllergy({ ...newAllergy, allergen: e.target.value })}
                disabled={!addAnotherAllergySelected}
                className={cn(!addAnotherAllergySelected && "bg-gray-100")}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="severity" className="text-sm">
                Severity
              </Label>
              <Select
                value={newAllergy.severity}
                onValueChange={(value) => setNewAllergy({ ...newAllergy, severity: value })}
                disabled={!addAnotherAllergySelected}
              >
                <SelectTrigger className={cn(!addAnotherAllergySelected && "bg-gray-100")}>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mild">Mild</SelectItem>
                  <SelectItem value="moderate">Moderate</SelectItem>
                  <SelectItem value="severe">Severe</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="allergyMedication" className="text-sm">
                Medication for allergy (if any)
              </Label>
              <Input
                id="allergyMedication"
                placeholder="Enter medication"
                value={newAllergy.medication}
                onChange={(e) => setNewAllergy({ ...newAllergy, medication: e.target.value })}
                disabled={!addAnotherAllergySelected}
                className={cn(!addAnotherAllergySelected && "bg-gray-100")}
              />
            </div>
            {addAnotherAllergySelected ? (
              <div className="flex items-center justify-center w-full bg-white">
                <div className="fixed flex items-center justify-center p-4 bottom-2 w-[350px] md:w-[600px] bg-white ">
              <Button
                className="w-[90%] bg-blue-500 hover:bg-blue-600 mt-4"
                onClick={handleAddAnotherAllergy}
                disabled={!newAllergy.allergen || !newAllergy.severity}
              >
                Add Allergy
              </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center w-full bg-white">
                <div className="fixed flex items-center justify-center p-4 bottom-2 w-[350px] md:w-[600px] bg-white ">
              <Button
                className="w-[90%] bg-blue-500 hover:bg-blue-600 mt-4"
                onClick={handleAllergiesConfirm}
              >
                Continue
              </Button>
                </div>
              </div>
            )}
          </div>
        )

      case 5.8:
        return (
          <div className="mt-2 bg-gray-50 px-4 pt-4 pb-20 rounded-lg">
            <div className="space-y-6">
              <div className="space-y-2">
                <p className="text-sm">Have you felt any palpitations, dizziness, or chest discomfort recently?</p>
                <div className="grid grid-cols-2 gap-2">
                  <div 
                    className="border rounded-md p-3 flex items-center justify-center cursor-pointer"
                    onClick={() => handleCheckInAnswer('palpitations', true)}
                  >
                    <div className="flex items-center space-x-2">
                      <div className={cn(
                        "w-5 h-5 rounded-full border-2 flex items-center justify-center",
                        checkInAnswers['palpitations'] === true ? "border-blue-500 bg-blue-500" : "border-gray-400"
                      )}>
                        {checkInAnswers['palpitations'] === true && <div className="w-2.5 h-2.5 bg-white rounded-full" />}
                      </div>
                      <span>Yes</span>
                    </div>
                  </div>
                  <div 
                    className="border rounded-md p-3 flex items-center justify-center cursor-pointer"
                    onClick={() => handleCheckInAnswer('palpitations', false)}
                  >
                    <div className="flex items-center space-x-2">
                      <div className={cn(
                        "w-5 h-5 rounded-full border-2 flex items-center justify-center",
                        checkInAnswers['palpitations'] === false ? "border-blue-500 bg-blue-500" : "border-gray-400"
                      )}>
                        {checkInAnswers['palpitations'] === false && <div className="w-2.5 h-2.5 bg-white rounded-full" />}
                      </div>
                      <span>No</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-sm">Are any of your allergies currently causing symptoms (like sneezing, rashes, or breathing issues)?</p>
                <div className="grid grid-cols-2 gap-2">
                  <div 
                    className="border rounded-md p-3 flex items-center justify-center cursor-pointer"
                    onClick={() => handleCheckInAnswer('allergies', true)}
                  >
                    <div className="flex items-center space-x-2">
                      <div className={cn(
                        "w-5 h-5 rounded-full border-2 flex items-center justify-center",
                        checkInAnswers['allergies'] === true ? "border-blue-500 bg-blue-500" : "border-gray-400"
                      )}>
                        {checkInAnswers['allergies'] === true && <div className="w-2.5 h-2.5 bg-white rounded-full" />}
                      </div>
                      <span>Yes</span>
                    </div>
                  </div>
                  <div 
                    className="border rounded-md p-3 flex items-center justify-center cursor-pointer"
                    onClick={() => handleCheckInAnswer('allergies', false)}
                  >
                    <div className="flex items-center space-x-2">
                      <div className={cn(
                        "w-5 h-5 rounded-full border-2 flex items-center justify-center",
                        checkInAnswers['allergies'] === false ? "border-blue-500 bg-blue-500" : "border-gray-400"
                      )}>
                        {checkInAnswers['allergies'] === false && <div className="w-2.5 h-2.5 bg-white rounded-full" />}
                      </div>
                      <span>No</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-sm">Have your blood pressure or blood sugar levels been fairly stable lately?</p>
                <div className="grid grid-cols-2 gap-2">
                  <div 
                    className="border rounded-md p-3 flex items-center justify-center cursor-pointer"
                    onClick={() => handleCheckInAnswer('vitals', true)}
                  >
                    <div className="flex items-center space-x-2">
                      <div className={cn(
                        "w-5 h-5 rounded-full border-2 flex items-center justify-center",
                        checkInAnswers['vitals'] === true ? "border-blue-500 bg-blue-500" : "border-gray-400"
                      )}>
                        {checkInAnswers['vitals'] === true && <div className="w-2.5 h-2.5 bg-white rounded-full" />}
                      </div>
                      <span>Yes</span>
                    </div>
                  </div>
                  <div 
                    className="border rounded-md p-3 flex items-center justify-center cursor-pointer"
                    onClick={() => handleCheckInAnswer('vitals', false)}
                  >
                    <div className="flex items-center space-x-2">
                      <div className={cn(
                        "w-5 h-5 rounded-full border-2 flex items-center justify-center",
                        checkInAnswers['vitals'] === false ? "border-blue-500 bg-blue-500" : "border-gray-400"
                      )}>
                        {checkInAnswers['vitals'] === false && <div className="w-2.5 h-2.5 bg-white rounded-full" />}
                      </div>
                      <span>No</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>


            <div className="flex items-center justify-center w-full bg-white">
            <div className="fixed flex flex-col items-center justify-center p-4 bottom-2 w-[350px] md:w-[600px] bg-white ">
              <Button
                className="w-[90%] bg-blue-500 hover:bg-blue-600"
                onClick={handleCheckInConfirm}
              >
                Continue
              </Button>
              <button className="text-blue-500 text-sm text-center">Skip questions</button>
            </div>
           </div>
          </div>
        )

      case 6:
        return (
          <div className="mt-2 bg-gray-50 px-4 pt-4 pb-20 rounded-lg">
            <div className="space-y-6">
              {redFlagQuestions.map((question) => (
                <div key={question.id} className="space-y-2">
                  <p className="text-sm">{question.text}</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div 
                      className="border rounded-md p-3 flex items-center justify-center cursor-pointer"
                      onClick={() => handleRedFlagChange(question.id, true)}
                    >
                      <div className="flex items-center space-x-2">
                        <div className={cn(
                          "w-5 h-5 rounded-full border-2 flex items-center justify-center",
                          redFlagAnswers[question.id] === true ? "border-blue-500 bg-blue-500" : "border-gray-400"
                        )}>
                          {redFlagAnswers[question.id] === true && <div className="w-2.5 h-2.5 bg-white rounded-full" />}
                        </div>
                        <span>Yes</span>
                      </div>
                    </div>
                    <div 
                      className="border rounded-md p-3 flex items-center justify-center cursor-pointer"
                      onClick={() => handleRedFlagChange(question.id, false)}
                    >
                      <div className="flex items-center space-x-2">
                        <div className={cn(
                          "w-5 h-5 rounded-full border-2 flex items-center justify-center",
                          redFlagAnswers[question.id] === false ? "border-blue-500 bg-blue-500" : "border-gray-400"
                        )}>
                          {redFlagAnswers[question.id] === false && <div className="w-2.5 h-2.5 bg-white rounded-full" />}
                        </div>
                        <span>No</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {showUrgentCare && (
              <div className="bg-[#FFF5E6] flex flex-col items-center justify-center rounded-[28px] p-4 space-y-4 mt-4">
                <p className="text-[20px] font-medium">Urgent Attention Required</p>
                <div className="bg-white rounded-[24px] p-4">
                  Based on your answers, you may need immediate medical attention. Please call emergency services (911)
                  or go to your nearest emergency room.
                </div>
              </div>
            )}

           <div className="flex items-center justify-center w-full bg-white">
            <div className="fixed flex items-center justify-center p-4 bottom-0 w-[350px] md:w-[600px] bg-white ">
            <Button className="w-full bg-blue-500 hover:bg-blue-600 mt-4" onClick={handleRedFlagConfirm}>
              {showUrgentCare ? "I understand" : "Continue"}
            </Button>
            </div>
           </div>
          </div>
        )

      case 7:
        return (
          <div className="mt-2 bg-gray-50 p-4 rounded-lg">
            <div className="space-y-4">
              <div className="bg-white border rounded-lg p-4 space-y-4">

                <div className="flex items-center space-x-2 pt-2">
                  <Checkbox
                    id="consent"
                    checked={consentChecked}
                    onCheckedChange={(checked) => setConsentChecked(checked as boolean)}
                  />
                  <label htmlFor="consent" className="text-xs text-gray-600">
                    I have read the details and giving my consent to proceed with consultation
                  </label>
                </div>
              </div>
            </div>

            <div className="w-full mt-4">
              <Button
                className={cn("w-full", consentChecked ? "bg-blue-600" : "bg-blue-300")}
                disabled={!consentChecked}
                onClick={handleBookingComplete}
              >
                $50 - Proceed to Consultation Booking
              </Button>
            </div>
          </div>
        )

      default:
        return null
    }
  }

  const handleCheckInAnswer = (questionId: string, value: boolean) => {
    setCheckInAnswers(prev => ({
      ...prev,
      [questionId]: value
    }))
  }

  const handleCheckInConfirm = () => {
    // Add user message for all check-in answers at once
    const userMessageId = Date.now().toString()
    const answers = Object.entries(checkInAnswers).map(([questionId, value]) => {
      switch(questionId) {
        case 'palpitations':
          return `Have you felt any palpitations, dizziness, or chest discomfort recently? ${value ? 'Yes' : 'No'}`
        case 'allergies':
          return `Are any of your allergies currently causing symptoms? ${value ? 'Yes' : 'No'}`
        case 'vitals':
          return `Have your blood pressure or blood sugar levels been fairly stable lately? ${value ? 'Yes' : 'No'}`
        default:
          return ''
      }
    }).filter(Boolean).join('\n')

    setMessages(prev => [
      ...prev,
      {
        id: userMessageId,
        type: "user",
        content: answers
      }
    ])

    // Move to red flag questions
    // setBookingStep(6)
    setTimeout(() => {
      const bookingMessageId = Date.now().toString()
      setMessages((prev) => [
        ...prev,
        {
          id: bookingMessageId,
          type: "bot",
          content: "Please answer these red flag questions:",
          bookingStep: 6,
        },
      ])
      setActiveBookingMessageId(bookingMessageId)
    }, 500)
  }

  const handleAddAnotherAllergy = () => {
    if (newAllergy.allergen && newAllergy.severity) {
      setAllergies([...allergies, newAllergy])
      setNewAllergy({ allergen: '', severity: '', medication: '' })
      
      // Add user message for the new allergy
      const userMessageId = Date.now().toString()
      setMessages(prev => [
        ...prev,
        {
          id: userMessageId,
          type: "user",
          content: `Added another allergy: ${newAllergy.allergen} (${newAllergy.severity})${newAllergy.medication ? ` - Medication: ${newAllergy.medication}` : ''}`
        }
      ])

      // Ask again if they want to add another allergy
      setTimeout(() => {
        const bookingMessageId = Date.now().toString()
        setMessages((prev) => [
          ...prev,
          {
            id: bookingMessageId,
            type: "bot",
            content: "Do you want to add another allergy?",
            bookingStep: 5.5,
          },
        ])
        setActiveBookingMessageId(bookingMessageId)
      }, 500)
    }
  }

  const [newAllergy, setNewAllergy] = useState<Allergy>({ allergen: '', severity: '', medication: '' })
  const [addAnotherAllergySelected, setAddAnotherAllergySelected] = useState(false)

  const handleAddAllergy = () => {
    if (newAllergy.allergen && newAllergy.severity) {
      setAllergies([...allergies, newAllergy])
      setNewAllergy({ allergen: '', severity: '', medication: '' })
      setShowAllergyInputs(false)
      
      // Add user message to chat
      const userMessageId = Date.now().toString()
      setMessages((prev) => [
        ...prev,
        {
          id: userMessageId,
          type: "user",
          content: `Added allergy: ${newAllergy.allergen} (${newAllergy.severity})${newAllergy.medication ? ` - Medication: ${newAllergy.medication}` : ''}`,
        },
      ])
      
      // Ask if they want to add another allergy
      // setBookingStep(5.5)
      setTimeout(() => {
        const bookingMessageId = Date.now().toString()
        setMessages((prev) => [
          ...prev,
          {
            id: bookingMessageId,
            type: "bot",
            content: "Do you want to add another allergy?",
            bookingStep: 5.5,
          },
        ])
        setActiveBookingMessageId(bookingMessageId)
      }, 500)
    } else if (!showAllergyInputs) {
      handleAllergiesConfirm()
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="h-screen p-0 bg-[#0A2B5C] sm:max-w-[600px] w-full overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        <div className="flex flex-col h-full">
          <SheetHeader className="bg-[#0A2B5C] text-white p-4 flex-shrink-0">
            <div className="flex justify-between items-center">
              <SheetTitle className="text-white text-xl">Talk to Alfie</SheetTitle>
              <SheetClose className="text-white hover:text-gray-300">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </SheetClose>
            </div>
          </SheetHeader>

          <div className="bg-white rounded-t-2xl border-b shadow-xl py-4 flex justify-center flex-shrink-0">
            <Avatar className="h-10 w-10">
              <AvatarImage src={AlfieImage || "/placeholder.svg"} />
              <AvatarFallback className="bg-blue-400 text-white">
                <Sparkles className="h-8 w-8" />
              </AvatarFallback>
            </Avatar>
          </div>

          <div className="flex-1 bg-white overflow-y-auto p-4 space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.type === "user" ? "justify-end" : "justify-start"} flex-col`}
              >
                {message.type === "bot" && message.isLoading ? (
                  <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-3 w-fit">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-[#0000001A] rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                      <div className="w-2 h-2 bg-[#0000001A] rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                      <div className="w-2 h-2 bg-[#0000001A] rounded-full animate-bounce"></div>
                    </div>
                  </div>
                ) : message.type === "bot" ? (
                  <>
                  <div className="bg-gray-100 rounded-lg p-3 max-w-[85%]">
                    <p className="text-sm">{message.content}</p>
                  </div>
                  {typeof message.bookingStep === 'number' && message.bookingStep >= 0 && message.id !== [...messages].reverse().find(m => m.type === "bot" && !m.isLoading)?.id && (
                    <button
                      className="flex items-center text-black text-sm gap-1 mb-4"
                      onClick={() => handleRetryQuestion(message.bookingStep as number)}
                    >
                      <RefreshCcw className="h-4 w-4" />
                      Retry question
                    </button>
                  )}
                  </>
                ) : (
                  <div className="bg-blue-100 text-blue-800 rounded-lg p-3 max-w-[85%] self-end">
                    <p className="text-sm">{message.content}</p>
                  </div>
                )}
              </div>
            ))}

            {showDiagnosis && diagnosisData && !showBookingFlow && (
              <div className="bg-white border rounded-lg p-4 space-y-4">
                <div>
                  <p className="text-lg font-medium">
                    Possible Condition: <span className="text-blue-600">{diagnosisData.condition}</span>
                  </p>
                  <p className="mt-5 text-sm font-medium">Overview</p>
                  <p className="text-sm mt-1">{diagnosisData.description}</p>
                </div>

                {/* Image Section - Keep outside tabs */}
                <div className="grid grid-cols-3 gap-2 mt-4">
                  {diagnosisData?.imageUrls?.map((url, index) => (
                    <img
                      key={index}
                      src={url || "/placeholder.svg"}
                      alt={`Medical illustration ${index + 1}`}
                      className="rounded-lg w-[136px] h-[136px]"
                    />
                  ))}
                </div>

                {/* Symptoms Section - Add back outside tabs */}
                <div>
                  <p className="text-sm font-medium">Symptoms:</p>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    {diagnosisData?.symptoms?.length > 0 ? (
                      <ul className="list-disc pl-5 space-y-1 col-span-2">
                        {diagnosisData?.symptoms?.map((symptom, index) => (
                          <li key={index} className="text-sm">
                            {symptom}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-gray-500 col-span-2">No symptom information available.</p>
                    )}
                  </div>
                </div>

                <div>
                {/* Tab Headers */}
                <div className="flex border-b bg-[#F1F4F9] rounded-t-[16px]">
                  <button
                    className={cn(
                      "flex-1 py-2 text-center text-sm font-medium",
                      activeTab === 'causes' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'
                    )}
                    onClick={() => setActiveTab('causes')}
                  >
                    Causes
                  </button>
                  <button
                    className={cn(
                      "flex-1 py-2 text-center text-sm font-medium",
                      activeTab === 'diagnosis' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'
                    )}
                    onClick={() => setActiveTab('diagnosis')}
                  >
                    Diagnosis
                  </button>
                  <button
                    className={cn(
                      "flex-1 py-2 text-center text-sm font-medium",
                      activeTab === 'treatment' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'
                    )}
                    onClick={() => setActiveTab('treatment')}
                  >
                    Treatment
                  </button>
                </div>

                {/* Tab Content */}
                <div className="bg-[#F1F4F9] p-4 rounded-b-[16px]">
                  {activeTab === 'causes' && (
                    <div className="space-y-2">
                      {diagnosisData?.causes?.length > 0 ? (
                        <ul className="list-disc pl-5 space-y-1">
                          {diagnosisData?.causes?.map((cause, index) => (
                            <li key={index} className="text-sm">{cause}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-gray-500\">No causes information available.</p>
                      )}
                    </div>
                  )}

                  {activeTab === 'diagnosis' && (
                    <div className="space-y-2">
                      {diagnosisData?.diagnosis?.length > 0 ? (
                         <ul className="list-disc pl-5 space-y-1">
                          {diagnosisData?.diagnosis?.map((diag, index) => (
                            <li key={index} className="text-sm">{diag}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-gray-500\">No diagnosis information available.</p>
                      )}
                    </div>
                  )}

                  {activeTab === 'treatment' && (
                    <div className="space-y-2">
                      {diagnosisData?.treatments?.length > 0 ? (
                        <ul className="list-disc pl-5 space-y-1">
                          {diagnosisData?.treatments?.map((treatment, index) => (
                            <li key={index} className="text-sm">
                              <strong>{treatment.medication} ({treatment.category})</strong>: {treatment.instructions.join(', ')}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-gray-500">No treatment information available.</p>
                      )}
                    </div>
                  )}
                </div>
                </div>

                <div className="flex items-center space-x-2 pt-2">
                  <Checkbox
                    id="consent"
                    checked={consentChecked}
                    onCheckedChange={(checked) => setConsentChecked(checked as boolean)}
                  />
                  <label htmlFor="consent" className="text-xs text-gray-600">
                    I have read the details and giving my consent to proceed with consultation
                  </label>
                </div>

                <div className="w-full mt-4">
                  <Button
                    className={cn("w-full", consentChecked ? "bg-blue-600" : "bg-blue-300")}
                    disabled={!consentChecked}
                    onClick={proceedToBooking}
                  >
                    $50 - Proceed to Consultation Booking
                  </Button>
                </div>
              </div>
            )}

            {showDiagnosis && !diagnosisData && !showBookingFlow && (
              <div className="bg-[#FFF5E6] flex flex-col items-center justify-center rounded-[28px] p-4 space-y-4">
                <p className="text-[20px] font-medium">Urgent Attention Required</p>
                <div className="bg-white rounded-[24px] p-4">
                  We couldn't identify a specific diagnosis based on your symptoms, but it's important to seek medical
                  attention. Please consult a doctor immediately to discuss your concerns.
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <div className="bg-white max-h-[360px] relative overflow-auto hide-scrollbar">

            {showBookingFlow && (
                <div className="px-6 pt-4 pb-8 border-t">
                  {messages.find(m => m.id === activeBookingMessageId) && renderBookingStepUI(messages.find(m => m.id === activeBookingMessageId)!)}
                </div>
            )}

            {!showBookingFlow && (
              <div className="p-4">
                {currentAgent === "alfie" &&
                  (() => {
                    const lastBotMessage = [...messages]
                      .reverse()
                      .find((m) => m.type === "bot" && !m.isLoading && (m.suggestiveAnswers || []).length > 0)
                    if (!lastBotMessage?.suggestiveAnswers) return null

                    const reversedMessages = [...messages].reverse()
                    const lastMessage = reversedMessages[0]

                    const hasSuggestiveAnswers =
                      lastMessage?.id === lastBotMessage?.id && (lastBotMessage?.suggestiveAnswers?.length ?? 0) > 0

                    return (
                      <div className="px-6 pt-4 pb-8 bg-white shadow-lg border-t">
                        <div className="px-10 grid grid-cols-2 gap-6 items-center">
                          {lastBotMessage.suggestiveAnswers.map((answer) => {
                            const isSelected = selectedAnswers[lastBotMessage.id]?.includes(answer.id) || false
                            const selectionType = answer.type

                            if (hasSuggestiveAnswers) {
                              return (
                                <div
                                  key={answer.id}
                                  className={cn(
                                    "flex items-center gap-3 rounded-lg cursor-pointer transition-colors",
                                    isSelected ? "border-blue-500" : "bg-white border-gray-300",
                                  )}
                                  onClick={() =>
                                    handleSuggestiveAnswerSelection(lastBotMessage.id, answer.id, selectionType)
                                  }
                                >
                                  {selectionType === "single" ? (
                                    <div
                                      className={cn(
                                        "w-5 h-5 rounded-full border-2 flex items-center justify-center",
                                        isSelected ? "border-blue-500" : "border-gray-400",
                                      )}
                                    >
                                      {isSelected && <div className="w-2.5 h-2.5 bg-blue-500 rounded-full" />}
                                    </div>
                                  ) : (
                                    <div
                                      className={cn(
                                        "w-5 h-5 rounded border-2 flex items-center justify-center",
                                        isSelected ? "border-blue-500 bg-blue-500" : "border-gray-400",
                                      )}
                                    >
                                      {isSelected && (
                                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                          <path
                                            fillRule="evenodd"
                                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                            clipRule="evenodd"
                                          />
                                        </svg>
                                      )}
                                    </div>
                                  )}
                                  <span className="text-sm flex-1">{answer.text}</span>
                                </div>
                              )
                            }

                            return null
                          })}
                        </div>
                        <Button
                          onClick={() => (lastBotMessage ? handleSuggestiveAnswerSubmit(lastBotMessage.id) : undefined)}
                          className="w-full bg-[#4272DD] rounded-[10px] h-[46px] hover:bg-blue-700 text-white mt-4"
                          disabled={
                            !lastBotMessage || selectedAnswers[lastBotMessage.id]?.length === 0 || isSendingMessage
                          }
                        >
                          Continue
                        </Button>

                        {showUrgentCare && (
                          <div className="mt-4 flex flex-col gap-3">
                            <Button
                              onClick={() => navigate({ to: "/" })}
                              className="w-full bg-white text-gray-800 border hover:bg-gray-200"
                            >
                              Back to Home Page
                            </Button>
                            <Button
                              onClick={() => {
                                setMessages([])
                                setShowUrgentCare(false)
                                setCurrentAgent("alfie")
                              }}
                              className="w-full bg-[#4272DD] text-white hover:bg-blue-700"
                            >
                              Start New Treatment
                            </Button>
                          </div>
                        )}
                      </div>
                    )
                  })()}

                {currentAgent === "vector" &&
                  (() => {
                    const reversedMessages = [...messages].reverse()
                    const lastMessage = reversedMessages[0]
                    const lastBotMessage = reversedMessages.find((m) => m.type === "bot" && !m.isLoading)

                    const hasSuggestiveAnswers =
                      lastMessage?.id === lastBotMessage?.id && (lastBotMessage?.suggestiveAnswers?.length ?? 0) > 0

                    if (lastBotMessage?.content === "No response content") return null

                    if (hasSuggestiveAnswers) {
                      return (
                        <div className="px-6 pt-4 pb-8 bg-white shadow-lg border-t">
                          <div className="px-10 grid grid-cols-2 gap-6 items-center">
                            {lastBotMessage?.suggestiveAnswers?.map((answer) => {
                              const isSelected = selectedAnswers[lastBotMessage?.id ?? ""]?.includes(answer.id) || false
                              const selectionType = answer.type

                              return (
                                <div
                                  key={answer.id}
                                  className={cn(
                                    "flex items-center gap-3 rounded-lg cursor-pointer transition-colors p-2",
                                    isSelected ? "border-blue-500" : "bg-white border-gray-300",
                                  )}
                                  onClick={() =>
                                    handleSuggestiveAnswerSelection(lastBotMessage.id, answer.id, selectionType)
                                  }
                                >
                                  {selectionType === "single" ? (
                                    <div
                                      className={cn(
                                        "w-5 h-5 rounded-full border-2 flex items-center justify-center",
                                        isSelected ? "border-blue-500" : "border-gray-400",
                                      )}
                                    >
                                      {isSelected && <div className="w-2.5 h-2.5 bg-blue-500 rounded-full" />}
                                    </div>
                                  ) : (
                                    <div
                                      className={cn(
                                        "w-5 h-5 rounded border-2 flex items-center justify-center",
                                        isSelected ? "border-blue-500 bg-blue-500" : "border-gray-400",
                                      )}
                                    >
                                      {isSelected && (
                                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                          <path
                                            fillRule="evenodd"
                                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                            clipRule="evenodd"
                                          />
                                        </svg>
                                      )}
                                    </div>
                                  )}
                                  <span className="text-sm flex-1">{answer.text}</span>
                                </div>
                              )
                            })}
                          </div>
                          <Button
                            onClick={() =>
                              lastBotMessage ? handleSuggestiveAnswerSubmit(lastBotMessage.id) : undefined
                            }
                            className="w-full bg-[#4272DD] rounded-[10px] h-[46px] hover:bg-blue-700 text-white mt-4"
                            disabled={
                              !lastBotMessage?.id ||
                              selectedAnswers[lastBotMessage.id]?.length === 0 ||
                              isSendingMessage
                            }
                          >
                            Continue
                          </Button>
                        </div>
                      )
                    }

                    return (
                      <div className="space-y-3">
                        <div className="relative">
                          <textarea
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Type here"
                            className="w-full border rounded-lg p-3 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[80px]"
                            disabled={isSendingMessage}
                          />
                          <div className="absolute bottom-2 right-2 text-xs text-gray-400">{inputValue.length}/500</div>
                        </div>
                        <Button
                          onClick={handleSendMessage}
                          className={cn(
                            "w-full",
                            inputValue.trim() && !isSendingMessage ? "bg-blue-600" : "bg-blue-300",
                          )}
                          disabled={!inputValue.trim() || isSendingMessage}
                        >
                          {isSendingMessage ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin mr-2" />
                              Loading...
                            </>
                          ) : (
                            "Continue"
                          )}
                        </Button>
                      </div>
                    )
                  })()}

                {currentAgent !== "vector" &&
                  currentAgent !== "alfie" &&
                  (() => {
                    const lastBotMessage = [...messages]
                      .reverse()
                      .find((m) => m.type === "bot" && !m.isLoading && (m.suggestiveAnswers || []).length > 0)

                    if (lastBotMessage?.suggestiveAnswers) {
                      return (
                        <div className="px-6 pt-4 pb-8 bg-white shadow-lg border-t">
                          <div className="px-10 grid grid-cols-2 gap-6 items-center">
                            {lastBotMessage.suggestiveAnswers.map((answer) => {
                              const isSelected = selectedAnswers[lastBotMessage.id]?.includes(answer.id) || false
                              const selectionType = answer.type

                              return (
                                <div
                                  key={answer.id}
                                  className={cn(
                                    "flex items-center gap-3 rounded-lg cursor-pointer transition-colors p-2",
                                    isSelected ? "border-blue-500" : "bg-white border-gray-300",
                                  )}
                                  onClick={() =>
                                    handleSuggestiveAnswerSelection(lastBotMessage.id, answer.id, selectionType)
                                  }
                                >
                                  {selectionType === "single" ? (
                                    <div
                                      className={cn(
                                        "w-5 h-5 rounded-full border-2 flex items-center justify-center",
                                        isSelected ? "border-blue-500" : "border-gray-400",
                                      )}
                                    >
                                      {isSelected && <div className="w-2.5 h-2.5 bg-blue-500 rounded-full" />}
                                    </div>
                                  ) : (
                                    <div
                                      className={cn(
                                        "w-5 h-5 rounded border-2 flex items-center justify-center",
                                        isSelected ? "border-blue-500 bg-blue-500" : "border-gray-400",
                                      )}
                                    >
                                      {isSelected && (
                                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                          <path
                                            fillRule="evenodd"
                                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                            clipRule="evenodd"
                                          />
                                        </svg>
                                      )}
                                    </div>
                                  )}
                                  <span className="text-sm flex-1">{answer.text}</span>
                                </div>
                              )
                            })}
                          </div>
                          <Button
                            onClick={() =>
                              lastBotMessage ? handleSuggestiveAnswerSubmit(lastBotMessage.id) : undefined
                            }
                            className="w-full bg-[#4272DD] rounded-[10px] h-[46px] hover:bg-blue-700 text-white mt-4"
                            disabled={
                              !lastBotMessage?.id ||
                              selectedAnswers[lastBotMessage.id]?.length === 0 ||
                              isSendingMessage
                            }
                          >
                            Continue
                          </Button>
                        </div>
                      )
                    }

                    return null
                  })()}
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}
